/**
 * Proactive "approaching limit" upgrade nudge.
 *
 * The historic upgrade CTA is purely *reactive* — it fires only once a limit
 * has already been HIT (e.g. the create-project 4xx in {@link Dashboard}). This
 * banner is the *proactive* counterpart: it reads the EXISTING account-wide
 * `GET /account/usage` rollup (via the shared {@link useAccountUsage}), derives
 * the {@link LimitState} of EVERY metered dimension generically (so a future
 * cap — say an AI-cost budget — is picked up automatically), and gently nudges
 * the user toward `/pricing` while they are still merely *near* a wall, not
 * blocked by it.
 *
 * Copy adapts to the worst state:
 * - `approaching` → soft "You're close to your <metric> limit (X of Y)…"
 * - `over`        → harder "limit reached".
 *
 * Dismissal is persisted via the `@molecule/app-storage` adapter (NOT raw
 * localStorage), keyed by a {@link nudgeDismissSignature} so the banner
 * re-shows when a NEW metric crosses the threshold, a metric escalates
 * `approaching → over`, or a new billing period begins — but stays quiet for
 * the rest of a period the user has already acknowledged.
 *
 * Two layouts share one decision path, both rendered through the shared
 * {@link Banner} so the visuals can't drift from the other banners:
 * - `variant="banner"` (default) — full card for the Dashboard, above the
 *   project list.
 * - `variant="compact"` — a thin strip for the Workspace, directly below the
 *   workspace header.
 *
 * All percent / state / signature math lives in `utilities/usage-utilities.ts`
 * so it stays unit-testable; this component is the thin presentational shell.
 */

import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from '@molecule/app-react'
import { getProvider as getStorageProvider } from '@molecule/app-storage'

import { useAccountUsage } from '../hooks/useAccountUsage.js'
import {
  allowanceLabel,
  allowanceResetLine,
  type AllowanceWindow,
  billingCta,
  type BillingCtaAction,
  billingStateLine,
  githubRepoLinkLabel,
  MOLECULE_REPO_URL,
} from '../upgrade-copy.js'
import { extraUsageAvailable } from '../utilities/usage-billing-utilities.js'
import {
  formatStorage,
  formatUsageLabel,
  nextUtcDailyResetMs,
  nudgeDismissSignature,
  type NudgeMetric,
  nudgeStateFromUsage,
  tightMetrics,
  withoutNudgeMetric,
} from '../utilities/usage-utilities.js'
import { Banner } from './Banner.js'

/**
 * Storage key under which the last-dismissed nudge signature is persisted via
 * the `@molecule/app-storage` adapter (NOT raw localStorage). Mirrors
 * {@link ONBOARDING_DISMISSED_KEY}'s convention.
 */
export const USAGE_NUDGE_DISMISSED_KEY = 'mol_usage_nudge_dismissed'

interface UsageNudgeProps {
  /** Layout: full card for the Dashboard, thin strip for the Workspace. */
  variant?: 'banner' | 'compact'
}

/**
 * Back-compat tier category for an API that predates `planCategory`.
 *
 * Only the two plan keys that ARE categories can be resolved without
 * reimplementing `planKeyCategory` (which also has to unwrap usage-pack and
 * seat keys). Anything else returns undefined so the copy names NO tier —
 * silence beats telling a paying user they are on the free plan.
 *
 * @param plan - The raw plan key from the usage payload.
 * @returns 'anonymous' | 'free', or undefined when it cannot be known.
 */
function guestOrFreeCategory(plan: string | null | undefined): string | undefined {
  if (plan === 'anonymous') return 'anonymous'
  if (plan == null) return 'free'
  return undefined
}

/**
 * Human metric labels (i18n-keyed, with English fallbacks).
 *
 * The AI label depends on the tier: only the unpaid tiers have a DAILY
 * allowance, so `window` picks the noun phrase via the shared
 * {@link allowanceLabel} rather than every surface hardcoding "daily".
 */
function metricLabel(
  t: ReturnType<typeof useTranslation>['t'],
  key: string,
  window?: AllowanceWindow,
): string {
  switch (key) {
    case 'deployments':
      return t('usage.metric.deployments', undefined, { defaultValue: 'deployments' })
    case 'customDomains':
      return t('usage.metric.customDomains', undefined, { defaultValue: 'custom domains' })
    case 'storageMb':
      return t('usage.metric.storage', undefined, { defaultValue: 'storage' })
    case 'aiCost':
      return allowanceLabel(window)
    default:
      return key
  }
}

/**
 * "X of Y" usage figure for a metric, GB-promoting storage. AI usage renders
 * as the UNITLESS percent of the allowance — currency never appears on
 * AI-usage surfaces.
 */
function metricUsage(metric: NudgeMetric): string {
  if (metric.storage) {
    const limit =
      metric.limit >= 999999 || !Number.isFinite(metric.limit) || metric.limit < 0
        ? 'Unlimited'
        : formatStorage(metric.limit)
    return `${formatStorage(metric.used)} / ${limit}`
  }
  if (metric.cost) {
    const percent =
      metric.limit > 0 ? Math.min(100, Math.round((metric.used / metric.limit) * 100)) : 0
    return `${percent}%`
  }
  return formatUsageLabel(metric.used, metric.limit)
}

export function UsageNudge({ variant = 'banner' }: UsageNudgeProps) {
  const { t } = useTranslation()

  const { usage } = useAccountUsage()
  // The signature the user last dismissed. `undefined` = not yet loaded from
  // storage (don't flash the banner before we know); `null`/'' = nothing dismissed.
  const [dismissedSig, setDismissedSig] = useState<string | null | undefined>(undefined)

  // Load the persisted dismissal signature via the storage adapter.
  useEffect(() => {
    let cancelled = false
    getStorageProvider()
      .get<string>(USAGE_NUDGE_DISMISSED_KEY)
      .then((v) => {
        if (!cancelled) setDismissedSig(v ?? null)
      })
      .catch(() => {
        if (!cancelled) setDismissedSig(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // With extra usage enabled and available, an exhausted AI allowance does not
  // stop the user — work continues as metered usage — so "You've used this
  // month's AI allowance… Upgrade" is a false alarm. Drop the AI metric (both
  // states); the plan caps that DO stop work still nudge.
  const rollup = extraUsageAvailable(usage)
    ? withoutNudgeMetric(nudgeStateFromUsage(usage), 'aiCost')
    : nudgeStateFromUsage(usage)
  const signature = nudgeDismissSignature(usage, rollup)

  const dismiss = useCallback(() => {
    setDismissedSig(signature)
    getStorageProvider()
      .set(USAGE_NUDGE_DISMISSED_KEY, signature)
      .catch(() => {
        // Non-fatal: in-session state already hides it.
      })
  }, [signature])

  // Nothing to nudge about, storage not yet read, or already dismissed for this
  // exact signature → render nothing. A NEW/escalated metric or new period
  // produces a different signature, so the banner re-shows.
  if (dismissedSig === undefined) return null
  if (rollup.severity === 'none' || !signature) return null
  if (dismissedSig === signature) return null

  const tight = tightMetrics(rollup)
  const isOver = rollup.severity === 'over'
  // The worst (first) tight metric drives the lead copy.
  const lead = tight[0]

  // The AI allowance is the one tight metric that comes BACK on a timer — when
  // it's the (worst) over-limit metric, LEAD with when it returns rather than
  // only "Upgrade". The other metrics (deployments/domains/storage) are plan
  // caps that never refresh, so they keep the standard "limit reached" copy.
  //
  // The window varies by tier and the server states it (`aiMeter.window`): a
  // rolling day on the unpaid tiers, the billing period on the paid ones. Read
  // it — assuming a day both named the wrong span AND, via the tier fallback
  // below, told a paying user they were on the free plan.
  const aiWindow = usage?.aiMeter?.window

  // An exhausted AI allowance has SEVERAL different fixes and the meter cannot
  // tell them apart, so the server states the one that applies right now. When
  // it is anything other than a plain plan upgrade, both the copy and the button
  // follow it: an empty prepaid balance is not "you've used this month's
  // allowance — Upgrade", it is "your balance is empty — Add funds", and a
  // cap-paused account is told the cap paused it, not to buy a bigger plan.
  // `'none'` means nothing is blocking the user, so the ordinary plan-cap copy
  // stands (and the AI metric is usually already dropped above).
  const aiBillingAction = usage?.billingAction
  const aiCta =
    lead?.key === 'aiCost' &&
    typeof aiBillingAction === 'string' &&
    aiBillingAction !== 'upgrade' &&
    aiBillingAction !== 'none'
      ? billingCta({ billingAction: aiBillingAction })
      : undefined
  const aiStateLine = aiCta ? billingStateLine(aiCta.action) : undefined

  const message = lead
    ? isOver
      ? lead.key === 'aiCost'
        ? (aiStateLine ??
          // Name the tier that actually ran out, so the user knows WHICH
          // allowance they hit. The unpaid window is a PER-USER ROLLING 24h span
          // anchored at the user's first chargeable message (P3-13), NOT a
          // shared UTC midnight — so use the server's `aiMeter.resetsAt` (the
          // budget key's real TTL, the same figure `/cost` renders). Inventing a
          // midnight here contradicted it: late in the UTC day this banner said
          // "refreshes in under an hour" while `/cost` correctly said "in about
          // 21 hours". The midnight is only a last-resort fallback for a meter
          // with no window end, and only a daily window has one to guess at.
          allowanceResetLine(
            usage?.aiMeter?.resetsAt
              ? new Date(usage.aiMeter.resetsAt).getTime()
              : aiWindow === 'billing-period'
                ? undefined
                : nextUtcDailyResetMs(),
            undefined,
            // The server's own tier category — `plan` is a raw key (pack and
            // seat keys included), so collapsing it here would reimplement
            // planKeyCategory and get a paying user labelled "free". Older APIs
            // omit it: the two keys that ARE categories still resolve, and an
            // unrecognized paid key names no tier rather than the wrong one.
            usage?.planCategory ?? guestOrFreeCategory(usage?.plan),
            aiWindow,
          ))
        : t(
            'usage.nudge.over',
            { metric: metricLabel(t, lead.key, aiWindow), usage: metricUsage(lead) },
            {
              defaultValue: `You've reached your ${metricLabel(t, lead.key, aiWindow)} limit (${metricUsage(lead)}). Upgrade for more.`,
            },
          )
      : t(
          'usage.nudge.approaching',
          { metric: metricLabel(t, lead.key, aiWindow), usage: metricUsage(lead) },
          {
            defaultValue: `You're close to your ${metricLabel(t, lead.key, aiWindow)} limit (${metricUsage(lead)}). Upgrade for more.`,
          },
        )
    : ''

  const ctaLabel = isOver
    ? t('upgrade.upgrade', undefined, { defaultValue: 'Upgrade' })
    : t('upgrade.viewPlans', undefined, { defaultValue: 'View plans' })

  // A guest can't Upgrade without an account — signing up / logging in is what
  // lifts their cap (a real account has a bigger free budget) — so a guest nudge
  // leads with Sign up + Log in and NO Upgrade. Logged-in tiers keep Upgrade.
  const isGuest = usage?.plan === 'anonymous'

  // The top tier has nothing above it, so an Upgrade button there opens a plans
  // modal with nothing to sell. The rollup states the plan CATEGORY rather than
  // a next tier, and `team` is the top one.
  const noHigherTier = usage?.planCategory === 'team'

  // One CTA decision: the guest pair, else the server-stated billing action when
  // it is something other than a plan upgrade, else the plan CTA — dropped
  // entirely when there is no higher tier to sell.
  const ctaActions: BillingCtaAction[] | undefined = isGuest
    ? billingCta({ isGuest: true }).actions
    : aiCta
      ? aiCta.actions
      : noHigherTier
        ? undefined
        : [{ id: 'upgrade', label: ctaLabel, href: '/pricing', color: 'primary' }]

  /**
   * The nudge's established `data-mol-id` for a resolved CTA button, so the
   * stable ids survive the move to the shared resolver.
   *
   * @param action - The resolved CTA button.
   * @returns The `data-mol-id` to render it with.
   */
  const ctaMolId = (action: BillingCtaAction): string =>
    action.id === 'sign-up'
      ? 'usage-nudge-signup'
      : action.id === 'log-in'
        ? 'usage-nudge-login'
        : 'usage-nudge-cta'

  return (
    <Banner
      tone={isOver ? 'error' : 'warning'}
      variant={variant === 'compact' ? 'strip' : 'card'}
      molId={`usage-nudge-${variant}-${rollup.severity}`}
      // `owner_only` offers this viewer no button — billing follows the project
      // owner — so the note IS the way forward and rides on the message.
      message={aiCta?.note ? `${message} ${aiCta.note}` : message}
      actions={[
        // Canonical CTA colors (DESIGN.md): Upgrade/View plans + Sign up primary,
        // Log in success (mirroring the auth page), informational GitHub secondary.
        // In-app routes render as router Links (no full page load); an external
        // target (the support mailto) stays a plain anchor.
        ...(ctaActions ?? []).map((action) => ({
          label: action.label,
          ...(action.external ? { href: action.href, external: true } : { to: action.href }),
          color: action.color,
          molId: ctaMolId(action),
        })),
        {
          label: githubRepoLinkLabel(),
          href: MOLECULE_REPO_URL,
          external: true,
          color: 'secondary',
          molId: 'usage-nudge-github',
        },
      ]}
      onDismiss={dismiss}
      dismissMolId="usage-nudge-dismiss"
      style={variant === 'banner' ? { marginBottom: 32 } : undefined}
    />
  )
}
